import { useQuery } from '@tanstack/react-query';
import { Building2, MapPin, ShieldCheck, Wallet, type LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { CuentasInactivas } from './CuentasInactivas';
import { Barras, enDinero, enPorcentaje } from '@/shared/ui/graficos';

interface Parroquia {
  parroquia: string;
  ruralidad: string;
  clientes: number;
  cuentas: number;
  exonerados: number;
  mensual: number;
}

interface Panel {
  clientes: number;
  cuentas: number;
  exonerados: number;
  mensual: number;
  parroquias: Parroquia[];
}

export function PanelDeCatastro() {
  const panel = useQuery({
    queryKey: ['informe-catastro'],
    queryFn: () => api.get<Panel>('/informes/catastro/panel'),
  });

  if (panel.isLoading) {
    return <Cargando titulo="Cargando" detalle="contando el padrón por parroquia" />;
  }

  const datos = panel.data;
  if (!datos || datos.parroquias.length === 0) {
    return (
      <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
        No hay parroquias cargadas
      </p>
    );
  }

  const conExoneracion = datos.cuentas > 0 ? (datos.exonerados / datos.cuentas) * 100 : 0;

  const porImporte = [...datos.parroquias].sort((a, b) => b.mensual - a.mensual);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra
          icono={MapPin}
          rotulo="Clientes"
          valor={datos.clientes.toLocaleString('es-EC')}
          tono="text-blue-700"
          fondo="bg-blue-50"
        />
        <Cifra
          icono={Building2}
          rotulo="Predios"
          valor={datos.cuentas.toLocaleString('es-EC')}
          tono="text-violet-700"
          fondo="bg-violet-50"
          ayuda="Cuentas activas: a las inactivas no se les factura"
        />
        <Cifra
          icono={ShieldCheck}
          rotulo="Exonerados"
          valor={`${datos.exonerados.toLocaleString('es-EC')} · ${enPorcentaje(conExoneracion)}`}
          tono="text-amber-700"
          fondo="bg-amber-50"
          ayuda="Explican la diferencia entre lo que hay en el padrón y lo que se cobra"
        />
        <Cifra
          icono={Wallet}
          rotulo="Al mes"
          valor={enDinero(datos.mensual)}
          tono="text-marca-700"
          fondo="bg-marca-50"
          ayuda="Lo que se facturaría con las tarifas de hoy"
        />
      </div>

      <Seccion
        titulo="Cuánto factura cada parroquia"
        ayuda="Al mes, de mayor a menor. El recuento de predios está en la tabla"
      >
        <Barras datos={porImporte} claveEtiqueta="parroquia" claveValor="mensual" nombre="Al mes" />
      </Seccion>

      <Seccion titulo="Detalle por parroquia" ayuda="Padrón, exoneraciones e importe mensual">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Parroquia', 'Sector', 'Clientes', 'Predios', 'Exonerados', 'Al mes'].map(
                  (c, i) => (
                    <th
                      key={c}
                      scope="col"
                      className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                        i < 2 ? 'text-left' : 'text-right'
                      }`}
                    >
                      {c}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-armazon-100">
              {porImporte.map((p) => (
                <tr key={p.parroquia} className="transition-colors hover:bg-armazon-50/60">
                  <td className="px-3 py-2.5 text-xs text-armazon-700">{p.parroquia}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        p.ruralidad === 'RURAL'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {p.ruralidad}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {p.clientes.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums">
                    {p.cuentas.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs tabular-nums text-amber-700">
                    {p.exonerados.toLocaleString('es-EC')}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold tabular-nums">
                    {enDinero(p.mensual)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Seccion>

      <Seccion
        titulo="Cuentas inactivas"
        ayuda="Las dadas de baja, con su motivo y la deuda que les quedó"
      >
        <CuentasInactivas />
      </Seccion>
    </div>
  );
}

function Cifra({
  icono: Icono,
  rotulo,
  valor,
  tono,
  fondo,
  ayuda,
}: {
  icono: LucideIcon;
  rotulo: string;
  valor: string;
  tono: string;
  fondo: string;
  ayuda?: string;
}) {
  return (
    <div className={`rounded-2xl border border-white/60 p-4 ${fondo}`} title={ayuda}>
      <span className="mb-2 flex items-center gap-2">
        <Icono className={`size-4 ${tono}`} aria-hidden />
        <span className="text-[10px] font-bold uppercase tracking-wider text-armazon-500">
          {rotulo}
        </span>
      </span>
      <span className={`block truncate text-xl font-bold tabular-nums ${tono}`}>{valor}</span>
    </div>
  );
}
